/**
 * O que a TELA mostra de uma sessão de voz em TEMPO REAL — [spec-008 T030c].
 *
 * Mini-store reativo (`Vue` isolado, mesmo padrão de `estado-da-voz.js` e do
 * `GlobalBus`), **sem nenhuma lógica em cima**: quem escreve é
 * `destino-de-voz-tempo-real.js`, quem lê é o composer.
 *
 * ## Por que NÃO entrou em `estado-da-voz.js`
 *
 * `estado-da-voz.js` é COMPARTILHADO pelos dois transportes, e dois dos seus
 * campos têm observador armado desde a carga do módulo
 * (`transporte-por-turnos.js` instala `$watch` em `estadoDaFala` e
 * `sessaoDeVozAtiva`). Pendurar campo novo ali convida o próximo dev a
 * pendurar um watcher novo no mesmo objeto — que é exatamente o acoplamento
 * que a [T030b] precisou desarmar (ver `transporte-no-comando.js`). Estes
 * quatro campos são de UMA sessão em tempo real e ninguém mais os observa.
 *
 * ## Por que FORA da instância do componente
 *
 * Mesmo motivo do `motorDeVoz`: `view-chat.vue` tem DUAS instâncias
 * mutuamente exclusivas do composer (herói e rodapé, alternadas por `v-if`),
 * e a troca DESTRÓI uma e MONTA outra. Uma transcrição parcial em progresso —
 * ou um cartão esperando decisão — que vivesse no `data()` sumiria no meio da
 * frase quando o Modo de Voz expandisse o herói para o rodapé.
 *
 * ⚠️ **O `idProposta` é o ÚNICO dado de negócio que chega por aqui, e de
 * propósito** (D-13/N16): a voz REMETE ao cartão, e é a TELA que carrega
 * valor, cliente, ativo e data-base — lidos de onde já vivem (o store do
 * chat), nunca ditos pelo canal falado, que altera identificador em 6/11 das
 * medições (G-11).
 */
import Vue from 'vue'

export const vozTempoReal = new Vue({
  data() {
    return {
      /** O que o consultor está falando AGORA — some quando o turno fecha. */
      transcricaoParcial: '',
      /** O último turno fechado do consultor (`transcricaoFinal`). */
      transcricaoDoTurno: '',
      /**
       * `{idProposta}` — a escrita pedida por voz deixou uma decisão
       * esperando NA TELA (US-5). O transporte fala que existe e NUNCA a
       * confirma; quem decide é o cartão.
       */
      propostaAguardando: null,
      /**
       * `{segundosRestantes, motivo}` — falta pouco para o teto da sessão
       * paga, ou o foco se perdeu ([T035]). `null` quando o motivo passou:
       * aviso de "vai encerrar" que fica na tela depois de cancelado é
       * afirmação falsa.
       */
      avisoDeEncerramento: null,
    }
  },
})

/**
 * A trilha de áudio VIVA da sessão — o `MediaStream` do `audioRemoto`.
 *
 * ⚠️ **Fora do `Vue` reativo de propósito.** Não é dado de tela: é o handle
 * que o `<audio>` consome, e pô-lo em `data()` convidaria um `v-if`/watcher a
 * depender da identidade de um objeto do navegador.
 *
 * ★ E fora da INSTÂNCIA por um motivo medido: herói e rodapé são instâncias
 * diferentes do composer, e a troca destrói uma e monta outra
 * (`composerNoHeroi`, `view-chat.vue`). O `<audio>` da instância destruída
 * some com ela — sem guardar o fluxo aqui, a IA emudeceria no instante em que
 * o Modo de Voz expande o herói para o rodapé, que é justo o primeiro gesto
 * da conversa. Quem monta re-anexa (`mounted` de `send-message.vue`).
 */
let _fluxoRemoto = null

export const guardarFluxoRemoto = (fluxo) => {
  _fluxoRemoto = fluxo || null
}

/** O `MediaStream` da sessão corrente, ou `null` fora de sessão. */
export const fluxoRemotoAtual = () => _fluxoRemoto

/**
 * Devolve os quatro campos — e a trilha — ao estado de sessão nenhuma.
 *
 * Chamado no fim da sessão (`encerrada`, `queda`) e na abertura da seguinte:
 * herdar a transcrição ou o cartão de uma sessão anterior faria a tela
 * mostrar a fala de outra conversa.
 */
export const limparVozTempoReal = () => {
  vozTempoReal.transcricaoParcial = ''
  vozTempoReal.transcricaoDoTurno = ''
  vozTempoReal.propostaAguardando = null
  vozTempoReal.avisoDeEncerramento = null
  _fluxoRemoto = null
}
